import React, { Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { useSnackbar } from 'notistack';
import { useDropzone } from 'react-dropzone';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Container,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Tab,
    Tabs,
    TextField
} from '@mui/material';
import { Add, Attachment, Delete, Edit } from '@mui/icons-material';
import CheckboxList from 'components/common/CheckboxList';
import { UnderTextUploadButton } from 'components/dropZone/components';
import { a11yProps, TabPanels } from 'components/mui/Tabs';
import PreviewList from 'components/common/PreviewList';
import useSwiperTabs from 'hooks/useSwiperTabs';
import usePasteFile from 'hooks/usePasteFile';
import { CompanyModel, CompanyType } from 'models/CompanyModel';
import { MailTemplateModel } from 'models/Mailings';
import { VoyageModel, VoyageStatus } from 'models/VoyageModel';
import useMailTemplates from 'modules/mailings/hooks/useMailTemplates';
import auth from 'recoil/atoms/auth';
import companyList from 'recoil/atoms/companyList';
import voyageFamily from 'recoil/atoms/voyageFamily';
import Api from 'utils/api';
import { removeItemAtIndex } from 'utils/common';


const tabs = [
    'companies',
    'voyages',
];

const MailingsInner = () => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const companies = useRecoilValue(companyList(CompanyType.Shipper));
    const [companiesChecked, setCompaniesChecked] = useState(companies.map(() => false));
    const voyagePending = useRecoilValue(voyageFamily(VoyageStatus.Pending));
    const voyagesLoaded = useRecoilValue(voyageFamily(VoyageStatus.Loaded));
    const voyagesDeparted = useRecoilValue(voyageFamily(VoyageStatus.Departed));
    const voyages = useMemo(() => [...voyagePending, ...voyagesLoaded, ...voyagesDeparted], [voyagePending, voyagesLoaded, voyagesDeparted]);
    const [voyagesChecked, setVoyagesChecked] = useState(voyages.map(() => false));
    const [type, setType] = useQueryParam('type', withDefault(StringParam, 'companies'));
    const {index, setIndex, setSwiper} = useSwiperTabs(tabs.indexOf(type));
    const [attachments, setAttachments] = useState<File[]>([]);
    const inputRef = usePasteFile<HTMLTextAreaElement>(setAttachments);
    const {enqueueSnackbar} = useSnackbar();
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');
    const {
        templates,
        openAddTemplateModal,
        openEditTemplateModal,
        remove,
        openPreviewModal,
    } = useMailTemplates();

    const change = (idx: number, fromSwiper?: boolean) => {
        setIndex(idx, fromSwiper);
        setType(tabs[idx]);
    };

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let ids = type === 'companies' ?
            companies.filter((c, i) => companiesChecked[i]).map(c => c.id) :
            voyages.filter((v, i) => voyagesChecked[i]).map(v => v.id);

        Api.mailings
            .send(subject, content, attachments, type, ids)
            .then(() => {
                enqueueSnackbar(t('mail_sent'), {variant: 'success'});

                setSubject('');
                setContent('');
                setAttachments([]);
                setCompaniesChecked(old => old.map(() => false));
                setVoyagesChecked(old => old.map(() => false));
            });
    };

    const preview = () => {
        let ids = type === 'companies' ?
            companies.filter((c, i) => companiesChecked[i]).map(c => c.id) :
            voyages.filter((v, i) => voyagesChecked[i]).map(v => v.id);

        Api.mailings
            .send(subject, content, attachments, type, ids, true)
            .then(data => openPreviewModal(subject, data));
    };

    const id = 'recipients';

    const removeAttachment = (i: number) => setAttachments(old => removeItemAtIndex(old, i));

    const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
        onDropAccepted: (files: File[]) => setAttachments(old => [...old, ...files]),
        noKeyboard: true,
        noClick: true,
        multiple: true,
    });

    const addButton = my.permissions.has('mail templates create') ? (
        <IconButton
            aria-label="add template"
            onClick={openAddTemplateModal}
        >
            <Add/>
        </IconButton>
    ) : undefined;

    const editButton = my.permissions.has('mail templates update') ? (t: MailTemplateModel) => (
        <ListItemText
            sx={{textAlign: 'right'}}
            primary={
                <IconButton
                    aria-label="edit template"
                    onClick={() => openEditTemplateModal(t)}
                >
                    <Edit/>
                </IconButton>
            }
        />
    ) : () => null;

    const deleteButton = my.permissions.has('mail templates delete') ? (t: MailTemplateModel) => (
        <IconButton
            edge="end"
            aria-label="delete template"
            onClick={() => remove(t.id)}
        >
            <Delete/>
        </IconButton>
    ) : () => undefined;

    const applyTemplate = (t: MailTemplateModel) => {
        setSubject(t.subject);
        setContent(t.content);
    };

    return (
        <Container maxWidth="xl" sx={{mt: 2}}>
            <Grid container columnSpacing={2}>
                <Grid item xs={12} sm={3}>
                    {my.permissions.has('mail templates view') &&
                        <Card>
                            <CardHeader
                                title={t('saved_templates')}
                                action={addButton}
                            />

                            <List dense>
                                {templates?.map(t => (
                                    <ListItem
                                        key={t.id}
                                        secondaryAction={deleteButton(t)}
                                    >
                                        <ListItemText
                                            sx={{
                                                cursor: 'pointer',
                                                padding: '10px 0',
                                                '&:hover': {backgroundColor: 'grey.50'},
                                                '.MuiListItemText-primary': {
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                },
                                            }}
                                            onClick={() => applyTemplate(t)}
                                            primary={t.subject}
                                        />
                                        {editButton(t)}
                                    </ListItem>
                                ))}
                            </List>
                        </Card>
                    }
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Card>
                        <CardHeader
                            title={t('custom_mail_composer')}
                        />
                        <Box component="form" onSubmit={submit}>
                            <CardContent>
                                <TextField
                                    id="subject"
                                    label={t('subject')}
                                    required
                                    fullWidth
                                    autoFocus
                                    value={subject}
                                    onChange={e => setSubject(e.target.value)}
                                />

                                <Box {...getRootProps()}>
                                    <TextField
                                        id="content"
                                        label={t('content')}
                                        required
                                        fullWidth
                                        multiline
                                        rows={12}
                                        variant="outlined"
                                        sx={{backgroundColor: isDragActive ? 'grey.100' : undefined}}
                                        inputRef={inputRef}
                                        value={content}
                                        onChange={e => setContent(e.target.value)}
                                    />
                                    <UnderTextUploadButton onClick={open}>
                                        <Grid container justifyContent="space-between">
                                            <Grid item>
                                                {t('mail_attachment')}
                                            </Grid>
                                            <Grid item><Attachment sx={{display: 'block'}}/></Grid>
                                        </Grid>
                                    </UnderTextUploadButton>
                                    <input {...getInputProps()} />
                                </Box>

                                <PreviewList
                                    attachments={attachments}
                                    remove={removeAttachment}
                                />
                            </CardContent>

                            <CardActions sx={{justifyContent: 'flex-end'}}>
                                <Button onClick={preview}>{t('preview')}</Button>
                                <Button type="submit">{t('send')}</Button>
                            </CardActions>
                        </Box>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Card>
                        <CardHeader
                            title={t('recipients')}
                        />

                        <Tabs
                            value={index}
                            onChange={(e, v) => change(v)}
                            aria-label="Recipient tabs"
                        >
                            <Tab label={t('companies')} {...a11yProps(id, 0)} />
                            <Tab label={t('voyages')} {...a11yProps(id, 1)} />
                        </Tabs>

                        <TabPanels
                            active={index}
                            id={id}
                            setIndex={change}
                            setSwiper={setSwiper}

                            tabs={[
                                <Box sx={{maxHeight: 'calc(100vh - 228px)', overflowX: 'auto'}}>
                                    <CheckboxList
                                        items={companies}
                                        formatter={c => c.name}
                                        checked={companiesChecked}
                                        setChecked={setCompaniesChecked}
                                    />
                                </Box>,
                                <Box sx={{maxHeight: 'calc(100vh - 228px)', overflowX: 'auto'}}>
                                    <CheckboxList
                                        items={voyages}
                                        formatter={v => `${v.number}, ${v.pol.code} - ${v.pod.code}`}
                                        checked={voyagesChecked}
                                        setChecked={setVoyagesChecked}
                                    />
                                </Box>,
                            ]}
                        />
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
};

const Mailings = () => {
    return (
        <Suspense fallback={null}>
            <MailingsInner/>
        </Suspense>
    )
};

export default Mailings;
